<template>
  <div class="post-preview-cell">
    <!-- 投稿画像サムネイル（プレビュー表示 ON 時のみ） -->
    <div v-if="previewEnabled" class="post-thumbnail">
      <img v-if="row.post.previewPostMediaUrl" :src="row.post.previewPostMediaUrl" alt="投稿画像" />
      <div v-else class="no-image">NO IMAGE</div>
    </div>

    <!-- 右側: アカウント情報 + 投稿文章 + アクション + タグ -->
    <div class="post-right">
      <div v-if="row.account" class="post-account-info">
        <Account :name="row.account.screenName" :image="row.account.pictureUrl" symbol="X" :is-display-name="false" />
        <div class="post-account-detail">
          <span class="post-account-name">@{{ row.account.screenName }}</span>
          <span class="post-meta">フォロワー数: {{ formatNumber(row.account.followersCount) }}</span>
          <span class="post-meta">{{ industryLabel }}</span>
        </div>
      </div>

      <div v-if="previewEnabled" ref="postTextWrapper" class="post-text-wrapper">
        <div class="post-text">{{ row.post.postText || '-' }}</div>
      </div>

      <div class="post-actions">
        <button v-if="row.post.postUrl" class="post-action-link" @click.stop="openPostUrl">
          <i class="mdi mdi-open-in-new" /><span>SNSで確認</span>
        </button>
        <button class="post-action-link" @click.stop="onAssignTag">
          <i class="mdi mdi-tag-multiple" /><span>タグ設定</span>
        </button>
      </div>

      <div v-if="row.tags && row.tags.length > 0" class="post-tags">
        <el-tag
          v-for="tag in row.tags.slice(0, 2)"
          :key="tag.id"
          size="mini"
          class="post-tag--gray"
          disable-transitions
        >
          {{ tag.name }}
        </el-tag>
        <el-tag v-if="row.tags.length > 2" size="mini" class="post-tag--gray" disable-transitions>
          +{{ row.tags.length - 2 }}
        </el-tag>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, PropType, ref, watch } from 'vue'

import Account from '@/client/components/_molecules/Account.vue'
import { TrackingService } from '@/client/services'
import { type PostListResponse } from '@/typings/twitter_benchmark_campaign_posts'

import { formatNumber } from '../../../utils/formatters'

type PostRow = PostListResponse['posts'][number]

/**
 * 投稿プレビュー列（固定・カスタマイズ対象外）のセル描画。アカウント情報・アクションリンク・タグは常時表示し、プレビュー ON 時のみサムネイルと本文を足す。
 */
export default defineComponent({
  name: 'TwBenchmarkPostPreviewCell',

  components: {
    Account
  },

  props: {
    row: {
      type: Object as PropType<PostRow>,
      required: true
    },
    previewEnabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['assign-tag'],

  setup(props, { emit }) {
    const postTextWrapper = ref<HTMLElement | null>(null)

    /**
     * 投稿内容のスクロールをテーブル側に伝播させないようにするための処理
     */
    const onWheel = (event: WheelEvent): void => {
      const el = postTextWrapper.value
      if (!el) return

      const scrollableAmount = el.scrollHeight - el.clientHeight
      if (scrollableAmount <= 0) return

      const LINE_HEIGHT_PX = 16
      let delta = event.deltaY
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        delta *= LINE_HEIGHT_PX
      } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        delta *= el.clientHeight
      }

      const nextScrollTop = Math.min(Math.max(el.scrollTop + delta, 0), scrollableAmount)
      if (nextScrollTop === el.scrollTop) {
        return
      }

      el.scrollTop = nextScrollTop
      event.preventDefault()
      event.stopPropagation()
    }

    watch(
      postTextWrapper,
      (el, _prevEl, onInvalidate) => {
        if (!el) return
        el.addEventListener('wheel', onWheel, { passive: false })
        onInvalidate(() => el.removeEventListener('wheel', onWheel))
      },
      { immediate: true }
    )

    onBeforeUnmount(() => {
      postTextWrapper.value?.removeEventListener('wheel', onWheel)
    })

    const industryLabel = computed<string>(() => {
      const industry = props.row.industry
      if (!industry) return '-'
      return `${industry.majorCategory}/${industry.minorCategory}`
    })

    const openPostUrl = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|SNSで確認')
      if (props.row.post.postUrl) {
        window.open(props.row.post.postUrl, '_blank', 'noopener')
      }
    }

    const onAssignTag = (): void => {
      TrackingService.sendEvent('click:競合キャンペーン分析|タグ設定')
      emit('assign-tag', props.row.post.id)
    }

    return {
      postTextWrapper,
      industryLabel,
      formatNumber,
      openPostUrl,
      onAssignTag
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.post-preview-cell {
  display: flex;
  gap: $spacing-unit;
  padding: 0;
}

.post-thumbnail {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  overflow: hidden;
  background: $background-color;
  border: 1px solid $border-color;
  border-radius: $round;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .no-image {
    font-size: 10px;
    color: $sub-text-color;
    text-align: center;
  }
}

.post-right {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: $spacing-unit * 0.5;
  min-width: 0;
}

:deep(.post-account-info) {
  display: flex;
  gap: $spacing-unit;
  align-items: flex-start;
}

.post-account-detail {
  display: block;

  .post-account-name {
    display: block;
    font-size: $font-small-size;
    font-weight: bold;
    line-height: 18px;
  }

  .post-meta {
    display: block;
    font-size: 11px;
    line-height: 18px;
    color: $text-color;
  }
}

.post-text-wrapper {
  flex: 1;
  max-height: 80px;
  padding: $spacing-unit * 0.5;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  background: $background-color;
  border-radius: $round;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.post-text {
  font-size: $font-small-size;
  line-height: 1.4;
  color: $text-color;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.post-actions {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}

.post-action-link {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 0;
  font-size: 12px;
  color: $sub-text-color;
  cursor: pointer;
  background: none;
  border: none;

  .mdi {
    width: auto;
    height: auto;
    margin: 0;
    font-size: 14px;
    line-height: 1;

    &::before {
      display: block;
      line-height: 1;
    }
  }

  &:hover {
    color: $primary-color;
  }
}

.post-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;

  .el-tag {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .post-tag--gray {
    color: $sub-text-color;
    background-color: #f4f4f5;
    border-color: #e9e9eb;
  }
}
</style>
